import React, { useEffect, useState } from 'react';
import { ReportingService } from '../ReportingService';
import { Report } from '../../../model/report/Report';
import { toast } from 'react-toastify';
import { Button, Table } from 'react-bootstrap';
import { SUCCESSFULLY } from '../../../enum/Messages';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { formatDate, getPlatformValue } from '../../../utils/Functions';
import ConfirmModal from './ConfirmModal';
import ReportingFilters from './ReportingFilters';
import { ErrorToast } from '../../../utils/toasters';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

const REPORTING_COLUMNS: ITableColumn[] = [
    { label: 'Campaign' },
    { label: 'Date' },
    { label: 'Influencer' },
    { label: 'Offer' },
    { label: 'Reported' },
    { label: 'Actions' },
];

export function ReportingOverview() {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [filterData, setFilterData] = useState({
        page: 1,
        perPage: 15,
        totalPages: 0,
        campaignId: undefined,
    });
    const [showModal, setShowModal] = useState({
        showModal: false,
        itemData: {},
    });
    const [reports, setReports] = useState<Report[]>([]);
    const [reportingTrigger, setReportingTrigger] = useState(false);

    useEffect(() => {
        let isCurrent = true;
        setDataLoading(true);

        ReportingService.getReports({ ...filterData, page: !!filterData.campaignId ? 1 : filterData.page })
            .then(data => {
                if (!isCurrent) return;
                setReports(data.data);
                setFilterData(prev => ({
                    ...prev,
                    totalPages: Math.ceil(data.count / filterData.perPage),
                }));
            })
            .catch(err => {
                if (isCurrent) ErrorToast(err);
            })
            .finally(() => {
                if (isCurrent) setDataLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [filterData.page, reportingTrigger]);

    function remindReport(id: number) {
        ReportingService.reportsRemind(id)
            .then(() => {
                toast(SUCCESSFULLY);
                setShowModal(prev => ({ ...prev, showModal: false }));
            })
            .catch(err => ErrorToast(err));
    }

    const handlePages = (updatePage: number) => {
        setFilterData(prev => ({
            ...prev,
            page: updatePage,
        }));
    };

    return (
        <>
            <ConfirmModal setShowModal={setShowModal} {...showModal} func={remindReport} />
            <ReportingFilters setPagination={setFilterData} setReportingTrigger={setReportingTrigger} />

            <TableLayout
                handlePages={handlePages}
                pagination={filterData}
                listLength={reports.length}
                isLoading={dataLoading}
                dataName={'reportings'}
                skeletonColumns={REPORTING_COLUMNS}
                skeletonRows={filterData.perPage}
            >
                <Table hover responsive bordered>
                    <thead>
                        <tr>
                            {REPORTING_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {reports?.map(report => (
                            <tr key={report?.id + '-' + report?.title}>
                                <td>{report?.title}</td>
                                <td align="center">
                                    {formatDate(report?.publishFrom)} - {formatDate(report?.publishTo)}
                                </td>
                                <td>
                                    {report?.offers.map(offer => (
                                        <p key={offer?.user?.id + '-' + offer?.user?.updatedAt}>
                                            <ImpersonateEye id={offer?.user?.id ?? 0} />
                                            {offer?.user?.displayName}
                                        </p>
                                    ))}
                                </td>
                                <td>
                                    {report?.offers.map(offer =>
                                        offer?.offerItems.map(item => (
                                            <p key={item?.id}>
                                                {getPlatformValue(item?.platform)} - {item?.nbPosts}
                                            </p>
                                        ))
                                    )}
                                </td>
                                <td>
                                    {report?.offers.map(offer =>
                                        offer?.offerItems.map(item => (
                                            <p key={item?.id}>
                                                {item?.reportedCount} / {item?.nbPosts}
                                            </p>
                                        ))
                                    )}
                                </td>
                                <td>
                                    <Button
                                        onClick={() =>
                                            setShowModal(prev => ({
                                                ...prev,
                                                showModal: true,
                                                itemData: { id: report.id },
                                            }))
                                        }
                                    >
                                        Remind
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
